import { supabase } from '../services/supabase';

// Database migration utility to fix the 7 errors
export const runDatabaseMigration = async () => {
  console.log('🔧 Starting database migration to fix errors...');
  
  try {
    // Since we can't execute DDL directly, we'll create the missing data
    // The table structure should be created via Supabase dashboard or SQL editor
    
    console.log('📝 Checking and creating employee leave balances...');
    
    // First, let's try to create some sample data to test if the table exists
    const { data: employees, error: empError } = await supabase
      .from('employees')
      .select('id')
      .eq('status', 'active')
      .limit(5);
    
    if (empError) {
      throw new Error(`Error fetching employees: ${empError.message}`);
    }
    
    if (!employees || employees.length === 0) {
      console.log('ℹ️ No active employees found to initialize leave balances');
      return { 
        success: true, 
        message: 'No active employees found. Please create employees first.' 
      };
    }
    
    // Try to check if employee_leave_balances table exists by querying it
    const { data: existingBalances, error: balanceError } = await supabase
      .from('employee_leave_balances')
      .select('id')
      .limit(1);
    
    if (balanceError && balanceError.code === 'PGRST205') {
      // Table doesn't exist - we need to create it via SQL
      throw new Error(
        'The employee_leave_balances table does not exist. Please run the SQL migration script in Supabase SQL Editor first. ' +
        'Go to Supabase Dashboard > SQL Editor and run the fix_database_errors.sql script.'
      );
    }
    
    console.log('✅ employee_leave_balances table exists!');
    
    // Initialize leave balances for employees who don't have them
    const currentYear = new Date().getFullYear();
    const balanceInserts = employees.map(emp => ({
      employee_id: emp.id,
      year: currentYear,
      vacation_days_total: 15,
      vacation_days_used: 0,
      sick_days_total: 10,
      sick_days_used: 0,
      personal_days_total: 5,
      personal_days_used: 0,
      emergency_days_total: 3,
      emergency_days_used: 0,
      unpaid_days_used: 0,
      maternity_days_total: 105,
      maternity_days_used: 0,
      paternity_days_total: 7,
      paternity_days_used: 0
    }));
    
    const { error: insertError } = await supabase
      .from('employee_leave_balances')
      .upsert(balanceInserts, { 
        onConflict: 'employee_id,year',
        ignoreDuplicates: true 
      });
    
    if (insertError) {
      console.log('ℹ️ Some balances may already exist:', insertError.message);
    } else {
      console.log(`✅ Initialized leave balances for ${employees.length} employees`);
    }

    console.log('✅ Database migration completed successfully!');
    return { 
      success: true, 
      message: `Database migration completed successfully! Initialized leave balances for ${employees.length} employees.` 
    };
    
  } catch (error) {
    console.error('❌ Database migration failed:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Migration failed with unknown error'
    };
  }
};

// Function to run SQL script via Supabase (requires manual execution)
export const getSQLScript = () => {
  return `-- Fix Database Errors for Enhanced Leave Management and Security
-- Run this script in Supabase Dashboard > SQL Editor

-- 1. Add missing columns to leave_requests table
ALTER TABLE public.leave_requests 
ADD COLUMN IF NOT EXISTS is_paid BOOLEAN DEFAULT true,
ADD COLUMN IF NOT EXISTS days_requested INTEGER DEFAULT 0,
ADD COLUMN IF NOT EXISTS days_approved INTEGER DEFAULT 0;

-- 2. Update leave_type constraint
ALTER TABLE public.leave_requests 
DROP CONSTRAINT IF EXISTS leave_requests_leave_type_check;

ALTER TABLE public.leave_requests 
ADD CONSTRAINT leave_requests_leave_type_check 
CHECK (leave_type IN ('sick', 'vacation', 'personal', 'maternity', 'paternity', 'emergency', 'bereavement', 'other'));

-- 3. Create employee_leave_balances table
CREATE TABLE IF NOT EXISTS public.employee_leave_balances (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  employee_id UUID NOT NULL REFERENCES public.employees(id) ON DELETE CASCADE,
  year INTEGER NOT NULL,
  
  vacation_days_total INTEGER DEFAULT 15,
  vacation_days_used INTEGER DEFAULT 0,
  vacation_days_remaining INTEGER GENERATED ALWAYS AS (vacation_days_total - vacation_days_used) STORED,
  
  sick_days_total INTEGER DEFAULT 10,
  sick_days_used INTEGER DEFAULT 0,
  sick_days_remaining INTEGER GENERATED ALWAYS AS (sick_days_total - sick_days_used) STORED,
  
  personal_days_total INTEGER DEFAULT 5,
  personal_days_used INTEGER DEFAULT 0,
  personal_days_remaining INTEGER GENERATED ALWAYS AS (personal_days_total - personal_days_used) STORED,
  
  emergency_days_total INTEGER DEFAULT 3,
  emergency_days_used INTEGER DEFAULT 0,
  emergency_days_remaining INTEGER GENERATED ALWAYS AS (emergency_days_total - emergency_days_used) STORED,
  
  unpaid_days_used INTEGER DEFAULT 0,
  
  maternity_days_total INTEGER DEFAULT 105,
  maternity_days_used INTEGER DEFAULT 0,
  maternity_days_remaining INTEGER GENERATED ALWAYS AS (maternity_days_total - maternity_days_used) STORED,
  
  paternity_days_total INTEGER DEFAULT 7,
  paternity_days_used INTEGER DEFAULT 0,
  paternity_days_remaining INTEGER GENERATED ALWAYS AS (paternity_days_total - paternity_days_used) STORED,
  
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  
  UNIQUE(employee_id, year)
);

-- 4. Enable RLS and create policies
ALTER TABLE public.employee_leave_balances ENABLE ROW LEVEL SECURITY;

DROP POLICY IF EXISTS "Leave balances viewable by all" ON public.employee_leave_balances;
CREATE POLICY "Leave balances viewable by all" 
  ON public.employee_leave_balances FOR SELECT 
  USING (true);

DROP POLICY IF EXISTS "Leave balances editable by all" ON public.employee_leave_balances;
CREATE POLICY "Leave balances editable by all" 
  ON public.employee_leave_balances FOR ALL 
  USING (true);

-- 5. Grant permissions
GRANT ALL ON public.employee_leave_balances TO authenticated;
GRANT ALL ON public.employee_leave_balances TO anon;

-- 6. Create function to calculate working days
CREATE OR REPLACE FUNCTION calculate_working_days(start_date DATE, end_date DATE)
RETURNS INTEGER AS $$
DECLARE
  working_days INTEGER := 0;
  curr_date DATE := start_date;
BEGIN
  WHILE curr_date <= end_date LOOP
    -- Check if it's not a weekend (1=Monday, 7=Sunday)
    IF EXTRACT(DOW FROM curr_date) NOT IN (0, 6) THEN
      working_days := working_days + 1;
    END IF;
    curr_date := curr_date + 1;
  END LOOP;
  
  RETURN working_days;
END;
$$ LANGUAGE plpgsql;

-- 7. Create token_blacklist table for enhanced security
CREATE TABLE IF NOT EXISTS public.token_blacklist (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  token_hash VARCHAR(255) NOT NULL UNIQUE,
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE,
  blacklisted_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  expires_at TIMESTAMP WITH TIME ZONE NOT NULL,
  reason VARCHAR(100) DEFAULT 'logout',
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Create indexes for performance
CREATE INDEX IF NOT EXISTS idx_token_blacklist_token_hash ON public.token_blacklist(token_hash);
CREATE INDEX IF NOT EXISTS idx_token_blacklist_user_id ON public.token_blacklist(user_id);
CREATE INDEX IF NOT EXISTS idx_token_blacklist_expires_at ON public.token_blacklist(expires_at);

-- Enable RLS for token_blacklist
ALTER TABLE public.token_blacklist ENABLE ROW LEVEL SECURITY;

-- RLS policies for token_blacklist
DROP POLICY IF EXISTS "Users can manage their own blacklisted tokens" ON public.token_blacklist;
DROP POLICY IF EXISTS "Users can insert their own blacklisted tokens" ON public.token_blacklist;
DROP POLICY IF EXISTS "Users can read their own blacklisted tokens" ON public.token_blacklist;
DROP POLICY IF EXISTS "Users can delete their own blacklisted tokens" ON public.token_blacklist;

-- Allow authenticated users to insert token blacklist entries
-- This is more permissive for INSERT to handle logout scenarios
CREATE POLICY "Users can insert their own blacklisted tokens" 
  ON public.token_blacklist FOR INSERT 
  WITH CHECK (auth.uid() IS NOT NULL);

-- Users can only read their own blacklisted tokens
CREATE POLICY "Users can read their own blacklisted tokens" 
  ON public.token_blacklist FOR SELECT 
  USING (auth.uid() = user_id);

-- Users can only delete their own blacklisted tokens
CREATE POLICY "Users can delete their own blacklisted tokens" 
  ON public.token_blacklist FOR DELETE 
  USING (auth.uid() = user_id);

-- No UPDATE policy needed as tokens shouldn't be updated once blacklisted

DROP POLICY IF EXISTS "Admins can view all blacklisted tokens" ON public.token_blacklist;
CREATE POLICY "Admins can view all blacklisted tokens" 
  ON public.token_blacklist FOR SELECT 
  USING (EXISTS (
    SELECT 1 FROM public.users 
    WHERE id = auth.uid() AND role = 'admin'
  ));

-- Grant permissions
GRANT ALL ON public.token_blacklist TO authenticated;
GRANT SELECT ON public.token_blacklist TO anon;

-- 8. Create utility functions for token blacklist
CREATE OR REPLACE FUNCTION public.cleanup_expired_tokens()
RETURNS INTEGER AS $$
DECLARE
  deleted_count INTEGER;
BEGIN
  DELETE FROM public.token_blacklist 
  WHERE expires_at < NOW();
  
  GET DIAGNOSTICS deleted_count = ROW_COUNT;
  RETURN deleted_count;
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

CREATE OR REPLACE FUNCTION public.is_token_blacklisted(token_hash_param VARCHAR(255))
RETURNS BOOLEAN AS $$
BEGIN
  RETURN EXISTS (
    SELECT 1 FROM public.token_blacklist 
    WHERE token_hash = token_hash_param 
    AND expires_at > NOW()
  );
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

CREATE OR REPLACE FUNCTION public.blacklist_all_user_tokens(user_id_param UUID)
RETURNS INTEGER AS $$
DECLARE
  inserted_count INTEGER;
BEGIN
  -- This is a placeholder function - actual implementation would require
  -- access to current user sessions which is handled by the application
  INSERT INTO public.token_blacklist (token_hash, user_id, expires_at, reason)
  VALUES ('placeholder_' || user_id_param::text, user_id_param, NOW() + INTERVAL '1 year', 'logout_all');
  
  GET DIAGNOSTICS inserted_count = ROW_COUNT;
  RETURN inserted_count;
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

-- Success message
SELECT '✅ Database migration completed successfully! Enhanced security features added.' as message;`;
};

// Function to check if migration is needed
export const checkMigrationStatus = async () => {
  try {
    // Check if employee_leave_balances table exists
    const { data, error } = await supabase
      .from('employee_leave_balances')
      .select('id')
      .limit(1);
    
    if (error && error.code === 'PGRST205') {
      return { needsMigration: true, reason: 'employee_leave_balances table missing' };
    }
    
    // Check if leave_requests has required columns
    const { data: leaveData, error: leaveError } = await supabase
      .from('leave_requests')
      .select('id, is_paid, days_requested, days_approved')
      .limit(1);
    
    if (leaveError && leaveError.message.includes('column')) {
      return { needsMigration: true, reason: 'leave_requests missing columns' };
    }
    
    // Check if token_blacklist table exists
    const { data: tokenData, error: tokenError } = await supabase
      .from('token_blacklist')
      .select('id')
      .limit(1);
    
    if (tokenError && tokenError.code === 'PGRST205') {
      return { needsMigration: true, reason: 'token_blacklist table missing - enhanced security features required' };
    }
    
    return { needsMigration: false, reason: 'Database schema is up to date with enhanced security features' };
    
  } catch (error) {
    return { needsMigration: true, reason: `Error checking migration status: ${error instanceof Error ? error.message : 'Unknown error'}` };
  }
};